import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

type SetStatusToPendingDeletionResponse = {
  deletedCount: number;
  modifiedCount: number;
};

type UseSetStatusToPendingDeletionMutationResponse = {
  setStatusToPendingDeletion: SetStatusToPendingDeletionResponse;
};

type UseSetStatusToPendingDeletionMutation = [
  (cepId: string, mdmIds: string[]) => Promise<{ data: SetStatusToPendingDeletionResponse | null; error: string | null }>,
  MutationResult<UseSetStatusToPendingDeletionMutationResponse>,
];

export const setStatusToPendingDeletionMutationCall = gql`
  mutation setStatusToPendingDeletion($cepId: String!, $mdmIds: [String!]!) {
    setStatusToPendingDeletion(cepId: $cepId, mdmIds: $mdmIds) {
      deletedCount
      modifiedCount
    }
  }
`;

export const useSetStatusToPendingDeletionMutation = (): UseSetStatusToPendingDeletionMutation => {
  const [setStatusToPendingDeletion, state] = useMutation<UseSetStatusToPendingDeletionMutationResponse>(
    setStatusToPendingDeletionMutationCall,
    {
      fetchPolicy: 'network-only',
      context: { clientName: MAYA_GRAPHQL_ENDPOINT },
    },
  );

  const executeMutation = useCallback(
    async (cepId: string, mdmIds: string[] = []) => {
      try {
        const { data } = await setStatusToPendingDeletion({
          variables: {
            cepId,
            mdmIds,
          },
        });

        return { data: data?.setStatusToPendingDeletion || null, error: null };
      } catch (error: unknown) {
        if (error instanceof Error) {
          const customContext = extractApolloErrorDetails(error);
          Logger.logError({
            error,
            customContext,
            transactionName: 'setStatusToPendingDeletion.ts - useSetStatusToPendingDeletionMutation',
          });

          return { data: null, error: customContext.message as string };
        }

        return { data: null, error: null };
      }
    },
    [setStatusToPendingDeletion],
  );

  return [executeMutation, state];
};
